import { Button } from '@ordo/ui/button'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { Skeleton } from '@ordo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { authHeaders } from '@/api/http'
import type { SpecialDietAccommodationWire } from '@/api/special-diet-accommodations'
import { type Mealtime, MEALTIME_LABELS } from '@/lib/mealtime'
import { specialDietWeek } from '@/lib/special-diet-week'
import { submitSpecialDietWeek } from '@/lib/special-diet-week-submit'

interface MealOption {
  accommodationId: string
  menuItemId: string
  name: string
  priceCents: number
  currency: string
}
interface SchoolMealOrder {
  id: string
  serviceDate: string
  mealtime: string
  status: string
  orderedBy?: 'PARENT' | 'SCHOOL_ADMIN'
  canCancel?: boolean
  items: Array<{ menuItem: { name: string } }>
}
/** A saved payment draft is not a purchased meal. */
export function specialDietOrderAttribution(order: Pick<SchoolMealOrder, 'status' | 'orderedBy'>): string {
  if (order.status === 'DRAFT') {
    return 'Payment pending — School Admin'
  }
  return order.orderedBy === 'PARENT' ? 'Ordered by Parent' : 'Ordered by School Admin'
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  for (const [key, value] of Object.entries(authHeaders())) {
    headers.set(key, value)
  }
  const response = await fetch(url, { ...init, headers, credentials: 'include' })
  const body = await response.json()
  if (!response.ok) {
    throw new Error(body.message ?? 'Unable to load or save the meal.')
  }
  return body
}
export function SpecialDietOrderDialog({
  accommodation,
  locationId,
}: {
  accommodation: SpecialDietAccommodationWire
  locationId: string
}) {
  const defaultMealtime = accommodation.mealtimes[0] ?? 'LUNCH'
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [mealtime, setMealtime] = useState(accommodation.mealtimes[0] ?? 'LUNCH')
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [keys, setKeys] = useState<Record<string, string>>({})
  const dates = specialDietWeek(date)
  const [pending, setPending] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  useEffect(() => {
    setSelected({})
    setKeys({})
    setMessage(null)
    setDate('')
    setMealtime(defaultMealtime)
  }, [accommodation.id, locationId, defaultMealtime])
  const base = '/api/special-diet/school-orders'
  const scope = { locationId, studentUserId: accommodation.studentUserId ?? '' }
  const options = useQuery({
    queryKey: ['special-diet-school-options', scope, dates[0], mealtime],
    enabled: open && Boolean(date),
    queryFn: ({ signal }) =>
      Promise.all(
        dates.map(async (serviceDate) => ({
          date: serviceDate,
          ...(await request<{ items: MealOption[] }>(
            `${base}/options?${new URLSearchParams({ ...scope, serviceDate, mealtime })}`,
            { signal },
          )),
        })),
      ),
  })
  const orders = useQuery({
    queryKey: ['special-diet-school-orders', scope, date, mealtime],
    enabled: open && dates.length > 0,
    queryFn: async ({ signal }) => ({
      orders: (
        await Promise.all(
          dates.map((serviceDate) =>
            request<{ orders: SchoolMealOrder[] }>(
              `${base}?${new URLSearchParams({ ...scope, serviceDate, mealtime })}`,
              { signal },
            ),
          ),
        )
      ).flatMap((result) => result.orders),
    }),
  })
  const days = (options.data ?? []).map((day) => {
    const meals = day.items.filter((item) => item.accommodationId === accommodation.id)
    const existing = orders.data?.orders.find(
      (order) => order.serviceDate.slice(0, 10) === day.date && order.mealtime === mealtime,
    )
    const chosen = meals.find((item) => item.menuItemId === selected[day.date])
    return { ...day, meals, existing, chosen }
  })
  const chosenDays = days.filter((day) => day.chosen && !day.existing)
  function resetChoice() {
    setSelected({})
    setKeys({})
    setMessage(null)
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        Meals
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Special Diets weekly meals</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <form
            className="space-y-3"
            onSubmit={async (event) => {
              event.preventDefault()
              if (!chosenDays.length || pending || !orders.data) {
                return
              }
              setPending(true)
              setMessage(null)
              const attempts = { ...keys }
              for (const day of chosenDays) {
                attempts[day.date] ??= crypto.randomUUID()
              }
              setKeys(attempts)
              try {
                const result = await submitSpecialDietWeek(chosenDays, (day) =>
                  request<{ status: string }>(base, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      ...scope,
                      serviceDate: day.date,
                      mealtime,
                      accommodationId: accommodation.id,
                      menuItemId: day.chosen!.menuItemId,
                      expectedUnitPriceCents: day.chosen!.priceCents,
                      idempotencyKey: attempts[day.date],
                    }),
                  }),
                )
                if (result.error) {
                  setMessage(
                    `${result.completed} meals ordered. ${result.error} Completed days are kept; review the remaining days before retrying.`,
                  )
                } else {
                  setSelected({})
                  setMessage(`${result.completed} meals ordered for this week.`)
                }
              } catch (error) {
                setMessage(
                  error instanceof Error ? error.message : 'Refresh the week to check which meals were ordered.',
                )
              } finally {
                await Promise.all([options.refetch(), orders.refetch()])
                setPending(false)
              }
            }}>
            <p className="text-sm text-muted-foreground">
              Choose the week, then select the scheduled meals to order. One meal per day; the school pays the displayed
              prices.
            </p>
            <Label htmlFor="sd-order-date">Week containing</Label>
            <Input
              id="sd-order-date"
              type="date"
              value={date}
              min={accommodation.effectiveFrom.slice(0, 10)}
              onChange={(event) => {
                setDate(event.target.value)
                resetChoice()
              }}
              required
              disabled={pending}
            />
            <Label htmlFor="sd-order-meal">Mealtime</Label>
            <select
              id="sd-order-meal"
              className="w-full rounded-md border p-2"
              value={mealtime}
              onChange={(event) => {
                setMealtime(event.target.value)
                resetChoice()
              }}
              disabled={pending}>
              {accommodation.mealtimes.map((meal) => (
                <option key={meal} value={meal}>
                  {MEALTIME_LABELS[meal as Mealtime] ?? meal}
                </option>
              ))}
            </select>
            {date && options.isPending ? <Skeleton className="h-12 w-full" /> : null}
            {options.error && <p role="alert">{options.error.message}</p>}
            <div className="space-y-3" aria-label="Weekly special-diet menu">
              {days.map((day) => (
                <section key={day.date} className="space-y-2 rounded-lg border p-3">
                  <h3 className="font-medium">
                    {new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </h3>
                  {day.existing ? (
                    <p role="status">
                      {specialDietOrderAttribution(day.existing)} —{' '}
                      {day.existing.items.map((item) => item.menuItem.name).join(', ')}
                    </p>
                  ) : day.meals.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No approved meal available.</p>
                  ) : (
                    day.meals.map((meal) => (
                      <label key={meal.menuItemId} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selected[day.date] === meal.menuItemId}
                          disabled={pending || !orders.data || orders.isError}
                          onChange={(event) => {
                            setSelected((current) => ({
                              ...current,
                              [day.date]: event.target.checked ? meal.menuItemId : '',
                            }))
                            setKeys((current) => ({ ...current, [day.date]: crypto.randomUUID() }))
                            setMessage(null)
                          }}
                        />
                        {meal.name} ·{' '}
                        {new Intl.NumberFormat(undefined, { style: 'currency', currency: meal.currency }).format(
                          meal.priceCents / 100,
                        )}
                      </label>
                    ))
                  )}
                </section>
              ))}
            </div>
            {message && (
              <p role="status" className="text-sm">
                {message}
              </p>
            )}
            <Button
              type="submit"
              disabled={!chosenDays.length || pending || !orders.data || orders.isError || options.isError}>
              {pending ? 'Ordering…' : `Order selected week (${chosenDays.length} meals)`}
            </Button>
          </form>
          <section className="space-y-2">
            <h3 className="font-medium">Student orders</h3>
            {orders.isPending ? <Skeleton className="h-16 w-full" /> : null}
            {orders.error && <p role="alert">{orders.error.message}</p>}
            {(orders.data?.orders ?? []).map((order) => (
              <div key={order.id} className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm">
                <span>
                  {order.serviceDate.slice(0, 10)} · {order.mealtime}
                  <br />
                  {order.items.map((item) => item.menuItem.name).join(', ')} · {order.status}
                  <br />
                  {specialDietOrderAttribution(order)}
                </span>
                {order.canCancel !== false && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={pending}
                    onClick={async () => {
                      setPending(true)
                      try {
                        await request(`${base}/${encodeURIComponent(order.id)}/cancel`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ locationId }),
                        })
                        setKeys({})
                        await orders.refetch()
                        setMessage('Order cancelled.')
                      } catch (error) {
                        setMessage(error instanceof Error ? error.message : 'Unable to cancel this order.')
                      } finally {
                        setPending(false)
                      }
                    }}>
                    Cancel order
                  </Button>
                )}
              </div>
            ))}
          </section>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
